class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.observeCartChanges();
    this.onCartPageShow = event => {
      if (event.persisted) this.refreshCartFromServer();
    };
    this.onCartVisible = () => {
      if (document.visibilityState === 'visible') this.refreshCartFromServer();
    };
    window.addEventListener('pageshow', this.onCartPageShow);
    document.addEventListener('visibilitychange', this.onCartVisible);
  }

  observeCartChanges() {
    const cartItemsContainer = document.getElementById('CartDrawer');

    if (!cartItemsContainer) {
      console.warn('Cart items container not found for observer');
      return;
    }

    this.cartObserver = new MutationObserver(async (mutations) => {
      if (mutations.some(m => m.type === 'childList')) {
        await this.cleanupOrphanedCones();
      }
    });

    this.cartObserver.observe(cartItemsContainer, {
      childList: true,
      subtree: true
    });

  
    document.addEventListener('cart-drawer:open', () => {
      setTimeout(() => this.cleanupOrphanedCones(), 300);
    }, { once: false });
  }

  async cleanupOrphanedCones() {
  if (this.cleaningOrphanedCones || (window.cfCartAddsPending > 0 || window.cfCartUpdatesPending > 0)) return;
  this.cleaningOrphanedCones = true;
  try {
    const cartRes = await fetch('/cart.js');
    if (!cartRes.ok) throw new Error('Could not read cart');
    const cart = await cartRes.json();
    if ((window.cfCartAddsPending > 0 || window.cfCartUpdatesPending > 0)) return;

    if (!cart || !cart.items || cart.items.length === 0) return;

    const updates = {};

    cart.items.forEach(item => {
      const linkedId = item.properties?._linked_id;
      if (linkedId) {
        const parentExists = cart.items.some(yarnItem => {
          return yarnItem.variant_id == linkedId && !yarnItem.properties?._linked_id;
      });

        if (!parentExists) {
          updates[item.key] = 0;
        }
      }
    });

    if (Object.keys(updates).length === 0) return;

    if ((window.cfCartAddsPending > 0 || window.cfCartUpdatesPending > 0)) return;
    const updateRes = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });

    if (!updateRes.ok) throw new Error('Could not remove orphaned cones');

    const sectionsRes = await fetch('/?sections=cart-drawer,cart-icon-bubble');
    const sectionsJson = await sectionsRes.json();

    const drawer = this.closest('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      drawer.renderContents({ sections: sectionsJson });
    } else {
      const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
      if (drawerInner && sectionsJson['cart-drawer']) {
        drawerInner.innerHTML = sectionsJson['cart-drawer'];
      }
      const bubble = document.querySelector('#cart-icon-bubble');
      if (bubble && sectionsJson['cart-icon-bubble']) {
        bubble.innerHTML = sectionsJson['cart-icon-bubble'];
      }
    }

    console.log('Orphaned cones removed and drawer refreshed');

  } catch (err) {
    console.error('Error when clearing dependent cones:', err);
  } finally {
    this.cleaningOrphanedCones = false;
  }
}

  disconnectedCallback() {
    super.disconnectedCallback?.();
    if (this.cartObserver) {
      this.cartObserver.disconnect();
    }
    window.removeEventListener('pageshow', this.onCartPageShow);
    document.removeEventListener('visibilitychange', this.onCartVisible);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
      this.refreshCartFromServer();
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      sectionElement.innerHTML =
          this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }


  async refreshCartFromServer() {
    if (this.cfRefreshPending || window.cfCartAddsPending > 0 || window.cfCartUpdatesPending > 0) return;
    this.cfRefreshPending = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js', {cache:'no-store'});
      if (!response.ok) return;
      const cart = await response.json();
      if (window.cfCartAddsPending > 0 || window.cfCartUpdatesPending > 0) return;
      const inputs = Array.from(this.querySelectorAll('.quantity__input[data-line-key]'));
      const matches = inputs.length === cart.items.length && cart.items.every(item =>
        inputs.some(input => input.dataset.lineKey === item.key && Number(input.value) === item.quantity)
      );
      if (matches && this.classList.contains('is-empty') === (cart.items.length === 0)) return;
      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
        return;
      }
      const sectionsResponse = await fetch(root + '?sections=cart-drawer,cart-icon-bubble', {cache:'no-store'});
      if (!sectionsResponse.ok) return;
      const sections = await sectionsResponse.json();
      if (window.cfCartAddsPending > 0 || window.cfCartUpdatesPending > 0) return;
      const source = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');
      const sourceCart = source.querySelector('cart-drawer');
      const sourceInner = source.querySelector('#CartDrawer');
      if (!sourceCart || !sourceInner) return;
      const sourceInputs = Array.from(sourceCart.querySelectorAll('.quantity__input[data-line-key]'));
      if (sourceInputs.length !== cart.items.length || !cart.items.every(item =>
        sourceInputs.some(input => input.dataset.lineKey === item.key && Number(input.value) === item.quantity)
      )) return;
      const wasOpen = this.classList.contains('active');
      this.querySelector('#CartDrawer').innerHTML = sourceInner.innerHTML;
      this.classList.toggle('is-empty', sourceCart.classList.contains('is-empty'));
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      const bubble = document.getElementById('cart-icon-bubble');
      const bubbleSource = new DOMParser().parseFromString(sections['cart-icon-bubble'] || '', 'text/html').querySelector('.shopify-section');
      if (bubble && bubbleSource) bubble.innerHTML = bubbleSource.innerHTML;
      if (wasOpen) trapFocus(this.querySelector('.drawer__inner'), this.querySelector('.drawer__close'));
      window.dispatchEvent(new CustomEvent('cartChanged'));
    } catch (error) {
      console.error('Unable to refresh cart after returning to the store', error);
    } finally {
      this.cfRefreshPending = false;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
